import type { ReactNode } from "react";

/**
 * Панель — базовая поверхность макета: карточки, таблицы, списки строк.
 *
 * В `design-reference/css/components.css` это класс `.panel`: вертикальный
 * градиент, холодная полупрозрачная граница и световая линия по верхней
 * кромке. Модификаторы `--green/--cyan/--violet` меняют ТОЛЬКО цвет границы,
 * а зелёный ещё добавляет внутреннее свечение — заливка у всех одна.
 *
 * Ни радиуса, ни внутренних отступов у панели в макете нет: их задаёт место
 * применения, потому что одна и та же панель на разных экранах имеет разный
 * скруглённый угол. Здесь так же — `radius` пропом, паддинги классом.
 */
export type PanelTone = "neutral" | "green" | "cyan" | "violet" | "rose";
export type PanelRadius = "md" | "lg" | "xl";

const TONES: Record<PanelTone, string> = {
  neutral: "border-border-subtle",
  // Свечение внутрь есть только у зелёной: в макете это панели дохода и
  // статистики, которые обязаны читаться как «здесь деньги».
  green: "border-status-profit/20 shadow-[var(--glow-inset)_color-mix(in_srgb,var(--color-status-profit)_10%,transparent)]",
  cyan: "border-status-info/20",
  violet: "border-ai-violet/20",
  rose: "border-status-loss/25",
};

const RADII: Record<PanelRadius, string> = {
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
};

interface PanelProps {
  children: ReactNode;
  tone?: PanelTone;
  radius?: PanelRadius;
  /** Тег обёртки: панель бывает и секцией, и элементом списка, и статьёй. */
  as?: "div" | "section" | "article" | "ul" | "li";
  className?: string;
}

export function Panel({
  children,
  tone = "neutral",
  radius = "lg",
  as: Tag = "div",
  className,
}: PanelProps) {
  return (
    <Tag
      className={[
        "panel-surface",
        RADII[radius],
        TONES[tone],
        className ?? "",
      ].join(" ")}
    >
      {children}
    </Tag>
  );
}
